import {
  InvoiceStatus,
  PaymentMethod,
  PaymentMethods,
} from "../../../utils/constants";
import { formatCurrency, trimDecimal } from "../../../utils/utils";

// Half a cent: a balance at or below this counts as settled.
const PAID_TOLERANCE = 0.005;

const validPayments = (payments) => (payments || []).filter(Boolean);

export const sumPayments = (payments) =>
  trimDecimal(
    validPayments(payments).reduce(
      (accum, payment) => accum + (Number(payment.amount) || 0),
      0,
    ),
  );

/**
 * Balance left after the payments, never below zero. An overpayment settles
 * the invoice and nothing more; there is no credit handling.
 */
export const getBalance = (total, amountPaid) =>
  Math.max(0, trimDecimal((Number(total) || 0) - (Number(amountPaid) || 0)));

export const hasPayments = (invoice) =>
  validPayments(invoice?.payments).length > 0;

/**
 * Distinct check / reference numbers, kept on the invoice as its own field
 * because @searchable only filters on top-level fields.
 */
export const getPaymentReferences = (payments) => [
  ...new Set(
    validPayments(payments)
      .map((payment) => (payment.reference || "").trim())
      .filter(Boolean),
  ),
];

/**
 * Every field the invoice's composite sort keys are built from. An update that
 * sends status without the rest leaves the invoice out of the list indexes.
 */
export const getInvoiceKeyFields = (invoice) => ({
  id: invoice.id,
  active: invoice.active,
  reloadYardId: invoice.reloadYardId,
  invoiceNumber: invoice.invoiceNumber,
  accountId: invoice.accountId,
  accountName: invoice.accountName,
  dueDate: invoice.dueDate,
  status: invoice.status,
});

/**
 * Update fields for recording a payment on a PENDING invoice. A payment that
 * brings the balance to zero completes the invoice as of the payment date.
 */
export const addPayment = (invoice, payment) => {
  const payments = [...validPayments(invoice.payments), payment];
  const balance = getBalance(invoice.total, sumPayments(payments));
  const update = {
    payments,
    paymentReferences: getPaymentReferences(payments),
    balance,
  };

  if (balance <= PAID_TOLERANCE) {
    update.balance = 0;
    update.status = InvoiceStatus.PAID;
    update.paidDate = payment.paymentDate || new Date().toISOString();
    update.overDue = false;
  }

  return update;
};

/**
 * Update fields for deleting a payment. A PAID invoice goes back to PENDING
 * only when its payments had covered the total; one completed by hand after a
 * short payment stays completed.
 */
export const removePayment = (invoice, paymentId) => {
  const before = validPayments(invoice.payments);
  const payments = before.filter((payment) => payment.id !== paymentId);
  const balance = getBalance(invoice.total, sumPayments(payments));
  const update = {
    payments,
    paymentReferences: getPaymentReferences(payments),
  };

  if (invoice.status !== InvoiceStatus.PAID) {
    update.balance = balance;
    return update;
  }

  const paidByPayments =
    getBalance(invoice.total, sumPayments(before)) <= PAID_TOLERANCE;

  if (paidByPayments && balance > PAID_TOLERANCE) {
    update.balance = balance;
    update.status = InvoiceStatus.PENDING;
    update.paidDate = null;
  } else {
    update.balance = 0;
  }

  return update;
};

/**
 * Balance to save when the invoice form is saved, from the invoice as stored
 * now (`stored`) and the total being saved. Completed and voided invoices keep
 * their stored balance. Otherwise it is the new total less what has been paid:
 * the recorded payments, or for invoices without any, what the stored balance
 * says was paid (a balance an accounting sync wrote back). A missing stored
 * balance means nothing paid.
 */
export const getSaveBalance = (stored, total) => {
  if (
    stored?.status === InvoiceStatus.PAID ||
    stored?.status === InvoiceStatus.VOID
  )
    return stored.balance ?? 0;

  if (hasPayments(stored))
    return getBalance(total, sumPayments(stored.payments));

  const storedBalance = stored?.balance;
  const paid =
    storedBalance == null
      ? 0
      : Math.max(0, (Number(stored.total) || 0) - Number(storedBalance));
  return getBalance(total, paid);
};

/** Balance after Reopen: recorded payments still count, Complete does not. */
export const getReopenBalance = (invoice) =>
  getBalance(invoice.total, sumPayments(invoice.payments));

/**
 * Condition that holds only while the invoice is unchanged since `invoice` was
 * read. Every AppSync write stamps a new updatedAt.
 */
export const getVersionCondition = (invoice) =>
  invoice?.updatedAt
    ? { updatedAt: { eq: invoice.updatedAt } }
    : { updatedAt: { attributeExists: false } };

/**
 * Input and condition for writing `update` over the invoice as read. The input
 * never carries updatedAt: AppSync keeps a value sent in the input instead of
 * stamping a new one, and a stale stamp would let the next stale write through.
 */
export const buildVersionedUpdate = (invoice, update) => {
  const input = { ...getInvoiceKeyFields(invoice), ...update };
  delete input.updatedAt;
  return { input, condition: getVersionCondition(invoice) };
};

/** True when AppSync refused a write because its condition did not hold. */
export const isConditionFailure = (error) =>
  (error?.errors || []).some((err) =>
    `${err?.errorType || ""} ${err?.message || ""}`.includes(
      "ConditionalCheckFailed",
    ),
  );

// Plans for saveInvoiceChange: { update } to write, or { skip } with the reason
// nothing is written. They run against the invoice as just read, and again on
// the fresh read after a conflict.

/**
 * `payment` keeps its id across attempts, so a payment that is already on the
 * invoice (a save whose response was lost, then retried) is not added twice.
 */
export const planRecordPayment = (invoice, payment) => {
  if (validPayments(invoice.payments).some((p) => p.id === payment.id))
    return { skip: "recorded" };
  if (invoice.status !== InvoiceStatus.PENDING) return { skip: "notPending" };
  return { update: addPayment(invoice, payment) };
};

export const planRemovePayment = (invoice, paymentId) => {
  if (!validPayments(invoice.payments).some((p) => p.id === paymentId))
    return { skip: "missing" };
  if (invoice.status === InvoiceStatus.VOID) return { skip: "void" };
  return { update: removePayment(invoice, paymentId) };
};

export const planComplete = (invoice, paidDate) =>
  invoice.status === InvoiceStatus.PAID
    ? { skip: "completed" }
    : {
        update: {
          status: InvoiceStatus.PAID,
          paidDate,
          balance: 0,
          overDue: false,
        },
      };

export const planReopen = (invoice) =>
  invoice.status === InvoiceStatus.NEW
    ? { skip: "reopened" }
    : {
        update: {
          status: InvoiceStatus.NEW,
          paidDate: null,
          balance: getReopenBalance(invoice),
        },
      };

/**
 * The invoice form's save. The form owns its own fields; status, paid date,
 * overdue, balance and active belong to Record Payment, Complete, Reopen and
 * Void, so they come from the invoice as just read, written after the form's
 * copies so a page left open can't put back what one of those changed. A
 * voided invoice is not saved: the form is locked once an invoice is void.
 */
export const planFormSave = (formInput) => (invoice) => {
  if (invoice.status === InvoiceStatus.VOID) return { skip: "void" };
  return {
    update: {
      ...formInput,
      active: invoice.active,
      status: invoice.status,
      paidDate: invoice.paidDate,
      overDue: invoice.overDue,
      balance: getSaveBalance(invoice, formInput.total),
    },
  };
};

/**
 * What Complete leaves unpaid, for the history note. Null when the invoice has
 * no recorded payments or nothing is left to pay.
 */
export const getUnpaidOnComplete = (invoice) => {
  if (!hasPayments(invoice)) return null;
  const balance = getBalance(invoice.total, sumPayments(invoice.payments));
  return balance > PAID_TOLERANCE ? balance : null;
};

export const getPaymentMethodName = (method) =>
  PaymentMethods.find((choice) => choice.id === method)?.name || method || "";

/** "Check #4412", "ACH 99812", or just the method when there is no reference. */
export const describePaymentMethod = (payment) => {
  const name = getPaymentMethodName(payment?.method);
  const reference = (payment?.reference || "").trim();
  if (!reference) return name;
  if (payment?.method === PaymentMethod.CHECK) return `${name} #${reference}`;
  return name ? `${name} ${reference}` : reference;
};

export const describePayment = (payment) => {
  const method = describePaymentMethod(payment);
  const amount = formatCurrency(payment?.amount);
  return method ? `${amount} (${method})` : amount;
};
